import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, Globe, Lock, Trash2 } from "lucide-react";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import TagPicker from "../../../../shared/ui/form/TagPicker";
import { partitionTags } from "../../../../shared/ui/form/partitionTags";
import {
    CONTENT_LANGUAGES,
    languageLabelKey,
} from "../../../../shared/i18n/contentLanguages";
import { PRESENTING_PROBLEMS } from "../../../../shared/domain/presentingProblems";
import { THERAPY_TYPES } from "../../../../shared/domain/therapyTypes";
import {
    useCopyMeditation,
    useDeleteMeditation,
    useMeditation,
    usePublishMeditation,
    useUnpublishMeditation,
    useUpdateMeditation,
} from "../../application/useMeditations";
import { estimateFromScript } from "../../domain/readingSpeed";
import {
    BackgroundTrackId,
    MAX_PRESENTING_PROBLEMS,
    MAX_THERAPY_TYPES,
    MEDITATION_LICENSES,
    MeditationLicense,
    PresentingProblem,
    TherapyType,
    isDraftSaveable,
} from "../../domain/types";
import MeditationAudioSection from "../components/MeditationAudioSection";
import MeditationReadOnlyView from "../components/MeditationReadOnlyView";
import MeditationScriptSection from "../components/MeditationScriptSection";
import ReadingSpeedField from "../components/ReadingSpeedField";

/**
 * Coming back to a meditation.
 *
 * One page for the script, the recording and the tags: a meditation already
 * begun is not edited in a fixed order, and a therapist arriving to fix one
 * line should not walk back through the steps that made it. Starting one is
 * the other job, and lives in `MeditationWizardPage`.
 *
 * Both sections stay here whatever the meditation holds today: one that began
 * as text may grow a recording later, and the other way round.
 *
 * Publishing is deliberately not a switch beside the others. It is a decision
 * about other people seeing your work, so it asks for the two things that make
 * that safe — whose it is and on what terms — and says plainly what it does.
 */
export default function MeditationEditorPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { meditationId } = useParams<{ meditationId: string }>();

    const { data: stored, isLoading } = useMeditation(meditationId);
    const updateMeditation = useUpdateMeditation();
    const deleteMeditation = useDeleteMeditation();
    const publishMeditation = usePublishMeditation();
    const unpublishMeditation = useUnpublishMeditation();
    const copyMeditation = useCopyMeditation();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [script, setScript] = useState("");
    const [readingSpeed, setReadingSpeed] = useState(90);
    const [backgroundTrack, setBackgroundTrack] = useState<BackgroundTrackId>(
        BackgroundTrackId.NONE
    );
    const [backgroundTrackId, setBackgroundTrackId] = useState<string | null>(null);
    const [mix, setMix] = useState({ voiceVolume: 1, backgroundVolume: 0.25 });
    /** What the author says it runs to, in minutes; null until they say. */
    const [declaredMinutes, setDeclaredMinutes] = useState<number | null>(null);
    // Empty means the author has not said. Left as it was found rather than
    // defaulted here: a meditation written before the field existed says
    // nothing about its language, and opening it should not quietly claim one.
    const [language, setLanguage] = useState("");
    const [therapyTypes, setTherapyTypes] = useState<TherapyType[]>([]);
    const [customTherapyTypes, setCustomTherapyTypes] = useState<string[]>([]);
    const [problems, setProblems] = useState<PresentingProblem[]>([]);
    const [customProblems, setCustomProblems] = useState<string[]>([]);
    const [license, setLicense] = useState<MeditationLicense>(
        MeditationLicense.ALL_RIGHTS_RESERVED
    );
    const [authorName, setAuthorName] = useState("");
    /**
     * The recording as it stands in this edit. `undefined` means untouched,
     * `null` means the therapist removed it — the same distinction the API
     * draws, kept here so a rename never silently drops a recording.
     */
    const [audio, setAudio] = useState<
        { path: string; durationSeconds: number } | null | undefined
    >(undefined);
    const [error, setError] = useState<string | null>(null);

    // Fill the form once the stored meditation lands. Keyed on its id so
    // switching between two meditations reloads rather than keeping the first.
    useEffect(() => {
        if (!stored) return;
        setTitle(stored.title);
        setDescription(stored.description ?? "");
        setScript(stored.script ?? "");
        setReadingSpeed(stored.reading_speed_wpm);
        setBackgroundTrack(stored.background_track);
        setBackgroundTrackId(stored.background_track_id ?? null);
        setMix({
            voiceVolume: stored.voice_volume ?? 1,
            backgroundVolume: stored.background_volume ?? 0.25,
        });
        setDeclaredMinutes(
            stored.declared_duration_seconds
                ? Math.round(stored.declared_duration_seconds / 60)
                : null
        );
        setLanguage(stored.language ?? "");
        setTherapyTypes(stored.therapy_types);
        setCustomTherapyTypes(stored.custom_therapy_types ?? []);
        setProblems(stored.presenting_problems);
        setCustomProblems(stored.custom_presenting_problems ?? []);
        setLicense(stored.license);
        setAuthorName(stored.author_name ?? "");
        setAudio(undefined);
    }, [stored]);

    const hasStoredAudio = Boolean(stored?.has_audio) && audio !== null;

    /*
     * The editor shows the meditation it is editing, not everything a
     * meditation could be. A written one has no recorder to answer for and no
     * bed to choose; a recorded one has no prompter and no reading pace. The
     * one that carries both shows both.
     */
    const hasScript = Boolean(stored?.script);
    const hasAudio = Boolean(stored?.has_audio);
    const canSave = isDraftSaveable({
        title,
        script,
        audioPath: audio?.path ?? (hasStoredAudio ? "stored" : null),
    });

    const estimate = useMemo(
        () => estimateFromScript(script, readingSpeed),
        [script, readingSpeed]
    );

    const audioChanges = () => {
        if (audio === undefined) return {};
        if (audio === null) return { audio_path: null, audio_duration_seconds: null };
        return {
            audio_path: audio.path,
            audio_duration_seconds: audio.durationSeconds,
        };
    };

    const save = async () => {
        setError(null);
        const changes = {
            title: title.trim(),
            script: script.trim() ? script : null,
            description: description.trim() || null,
            reading_speed_wpm: readingSpeed,
            background_track: backgroundTrack,
            background_track_id: backgroundTrackId,
            declared_duration_seconds: declaredMinutes ? declaredMinutes * 60 : null,
            language: language || null,
            voice_volume: mix.voiceVolume,
            background_volume: mix.backgroundVolume,
            therapy_types: therapyTypes,
            custom_therapy_types: customTherapyTypes,
            presenting_problems: problems,
            custom_presenting_problems: customProblems,
            license,
            author_name: authorName.trim() || null,
            ...audioChanges(),
        };

        try {
            await updateMeditation.mutateAsync({
                id: meditationId as string,
                changes,
            });
            setAudio(undefined);
        } catch {
            setError(t("meditation.saveFailed"));
        }
    };

    const publish = async () => {
        setError(null);
        try {
            await publishMeditation.mutateAsync({
                id: meditationId as string,
                authorName: authorName.trim() || null,
                license,
            });
        } catch {
            setError(t("meditation.publishFailed"));
        }
    };

    const remove = async () => {
        await deleteMeditation.mutateAsync(meditationId as string);
        navigate("/meditations");
    };

    if (isLoading) {
        return <p className="text-sm text-content-secondary">{t("common.loading")}</p>;
    }

    // Somebody else's published meditation is readable, not editable: the
    // therapist who wants to change a line takes a copy first, and the copy is
    // theirs.
    if (stored && !stored.is_owner) {
        return (
            <MeditationReadOnlyView
                meditation={stored}
                onBack={() => navigate("/meditations?tab=public")}
                adding={copyMeditation.isPending}
                onOpenCopy={(copyId) => navigate(`/meditations/${copyId}`)}
                onAdd={async () => {
                    const copy = await copyMeditation.mutateAsync(stored.id);
                    navigate(`/meditations/${copy.id}`, { replace: true });
                }}
            />
        );
    }

    const saving = updateMeditation.isPending;

    return (
        <>
            <PageMeta
                title={t("meditation.title")}
                description={t("meditation.toolShort")}
            />

            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={() => navigate("/meditations")}
                    className="flex min-h-11 items-center gap-2 text-sm font-medium text-content-secondary hover:text-content-primary"
                >
                    <ArrowLeft size={16} aria-hidden="true" />
                    {t("meditation.backToLibrary")}
                </button>

                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        startIcon={<Trash2 size={16} />}
                        onClick={() => void remove()}
                        loading={deleteMeditation.isPending}
                    >
                        {t("common.delete")}
                    </Button>
                    <Button
                        onClick={() => void save()}
                        disabled={!canSave}
                        loading={saving}
                    >
                        {t("common.save")}
                    </Button>
                </div>
            </div>

            {error && (
                <p role="alert" className="mb-4 text-sm text-status-critical">
                    {error}
                </p>
            )}

            <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <div className="space-y-5">
                    <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                        <Label htmlFor="meditation-title">
                            {t("meditation.fields.title")}
                        </Label>
                        <input
                            id="meditation-title"
                            className="field-base field-default h-11 w-full px-4"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t("meditation.fields.titlePlaceholder")}
                        />

                        <div className="mt-4">
                            <Label htmlFor="meditation-description">
                                {t("meditation.fields.description")}
                            </Label>
                            <textarea
                                id="meditation-description"
                                rows={2}
                                className="field-base field-default w-full px-4 py-2.5"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t(
                                    "meditation.fields.descriptionPlaceholder"
                                )}
                            />
                        </div>

                        <div className="mt-4">
                            <Label htmlFor="meditation-language">
                                {t("meditation.fields.language")}
                            </Label>
                            <select
                                id="meditation-language"
                                className="field-base field-default h-11 w-full appearance-none px-4"
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                            >
                                <option value="">
                                    {t("meditation.fields.languageUnset")}
                                </option>
                                {CONTENT_LANGUAGES.map((code) => (
                                    <option key={code} value={code}>
                                        {t(languageLabelKey(code))}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </section>

                    {hasScript && (
                        <MeditationScriptSection
                            script={script}
                            onChange={setScript}
                            readingSpeed={readingSpeed}
                        />
                    )}

                    {hasAudio && (
                        <MeditationAudioSection
                            forCenter={Boolean(stored?.in_center_library)}
                            backgroundTrack={backgroundTrack}
                            backgroundTrackId={backgroundTrackId}
                            hasStoredAudio={hasStoredAudio}
                            meditationId={meditationId}
                            onAudioReady={(next) => setAudio(next)}
                            onAudioCleared={() => setAudio(null)}
                            voiceVolume={mix.voiceVolume}
                            backgroundVolume={mix.backgroundVolume}
                            onBackgroundTrackChange={(track, id, next) => {
                                setBackgroundTrack(track);
                                setBackgroundTrackId(id);
                                if (next) setMix(next);
                            }}
                        />
                    )}
                </div>

                <div className="space-y-5">
                    {hasScript && (
                        <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                            <ReadingSpeedField
                                value={readingSpeed}
                                onChange={setReadingSpeed}
                                estimatedSeconds={estimate.seconds}
                                declaredMinutes={declaredMinutes}
                                onDeclaredMinutesChange={setDeclaredMinutes}
                            />
                        </section>
                    )}

                    <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                        <Label>{t("meditation.fields.therapyTypes")}</Label>
                        <TagPicker
                            options={THERAPY_TYPES.map((type) => ({
                                value: type,
                                label: t(`session.therapyOptions.${type}`),
                            }))}
                            selected={[...therapyTypes, ...customTherapyTypes]}
                            max={MAX_THERAPY_TYPES}
                            allowCustom
                            browsable={false}
                            onChange={(next) => {
                                const { known, custom } = partitionTags(
                                    next,
                                    THERAPY_TYPES
                                );
                                setTherapyTypes(known as TherapyType[]);
                                setCustomTherapyTypes(custom);
                            }}
                            searchLabel={t("meditation.fields.therapyTypesPlaceholder")}
                            inputLabel={t("meditation.fields.therapyTypesPlaceholder")}
                            emptyLabel={t("meditation.fields.therapyTypesEmpty")}
                        />
                        <p className="mt-2 text-xs text-content-tertiary">
                            {t("meditation.fields.therapyTypesCustomHint")}
                        </p>

                        <div className="mt-4">
                            <Label>{t("meditation.fields.presentingProblems")}</Label>
                            <TagPicker
                                options={PRESENTING_PROBLEMS.map((problem) => ({
                                    value: problem,
                                    label: t(`publicDirectory.problems.${problem}`),
                                }))}
                                selected={[...problems, ...customProblems]}
                                max={MAX_PRESENTING_PROBLEMS}
                                allowCustom
                                browsable={false}
                                onChange={(next) => {
                                    const { known, custom } = partitionTags(
                                        next,
                                        PRESENTING_PROBLEMS
                                    );
                                    setProblems(known as PresentingProblem[]);
                                    setCustomProblems(custom);
                                }}
                                searchLabel={t(
                                    "meditation.fields.presentingProblemsPlaceholder"
                                )}
                                inputLabel={t(
                                    "meditation.fields.presentingProblemsPlaceholder"
                                )}
                                emptyLabel={t(
                                    "meditation.fields.presentingProblemsEmpty"
                                )}
                            />
                        </div>
                    </section>

                    {/* A centre's meditation stays in the centre: nothing to publish. */}
                    {stored?.in_center_library ? (
                        <p className="rounded-2xl border border-border-subtle bg-surface-raised p-4 text-sm text-content-secondary">
                            {t("meditation.centerLibraryNotice")}
                        </p>
                    ) : (
                        <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                            <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-content-primary">
                                {stored?.visibility === "public" ? (
                                    <Globe size={15} aria-hidden="true" />
                                ) : (
                                    <Lock size={15} aria-hidden="true" />
                                )}
                                {t("meditation.publish.title")}
                            </h2>
                            <p className="mb-4 text-xs text-content-tertiary">
                                {t("meditation.publish.explainer")}
                            </p>

                            <Label htmlFor="meditation-author">
                                {t("meditation.fields.authorName")}
                            </Label>
                            <input
                                id="meditation-author"
                                className="field-base field-default h-11 w-full px-4"
                                value={authorName}
                                onChange={(e) => setAuthorName(e.target.value)}
                                placeholder={t("meditation.fields.authorPlaceholder")}
                            />

                            <div className="mt-4">
                                <Label htmlFor="meditation-license">
                                    {t("meditation.fields.license")}
                                </Label>
                                <select
                                    id="meditation-license"
                                    className="field-base field-default h-11 w-full appearance-none px-4"
                                    value={license}
                                    onChange={(e) =>
                                        setLicense(e.target.value as MeditationLicense)
                                    }
                                >
                                    {MEDITATION_LICENSES.map((value) => (
                                        <option key={value} value={value}>
                                            {t(`meditation.licenses.${value}`)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mt-4">
                                {stored?.visibility === "public" ? (
                                    <Button
                                        variant="outline"
                                        fullWidth
                                        loading={unpublishMeditation.isPending}
                                        onClick={() =>
                                            void unpublishMeditation.mutateAsync(
                                                meditationId as string
                                            )
                                        }
                                    >
                                        {t("meditation.publish.withdraw")}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="secondary"
                                        fullWidth
                                        loading={publishMeditation.isPending}
                                        disabled={!authorName.trim()}
                                        onClick={() => void publish()}
                                    >
                                        {t("meditation.publish.action")}
                                    </Button>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </>
    );
}
